import React, {useCallback, useEffect, useState} from "react";
import {Col} from "reactstrap";
import {MuiRFF, Yup} from '../../../../../../../components/form';
import useIsMounted from "../../../../../../../hooks/use-is-mounted";
import ValidateMessages from "../../../../../../../../core/models/static/texts/vallidate-messages";
import Api from "../../../../../../../../core/services/api/api";
import {Paper} from "@mui/material";
import Form from "../../../../../../../components/base/form";
import {OutlinedInputForm} from "../../../../../../../components/base/input/outlined_input";
import Button from "../../../../../../../components/base/button/button";
import TryAgain from "../../../../../../../components/app-specific/try-again";
import WarrantyAndReturnStatusCard from "../../../../../../../components/app-specific/part-support/part-support-setting-cards/status-card";


const schema = Yup.object().shape({
    newStatus: Yup.string().trim().nullable().required(ValidateMessages.required)
})

const WarrantyAndReturnStatusesViewRequestStatusesSection = () => {
    const [statuses, setStatuses] = useState([])
    const [loading, setLoading] = useState(true)
    const [creating, setCreating] = useState(false)
    const isMounted = useIsMounted()
    const validate = MuiRFF.makeValidate(schema)
    const required = MuiRFF.makeRequired(schema)

    /**
     * Fetches the statuses and reasons from the server and populates the state list.
     */
    const getStatusesAndReasons = useCallback(async () => {
        setLoading(true)
        const response = await Api.getPartSupportStatusesAndReasonsForEdit();
        if (!isMounted()) return
        if (response?.resultFlag) {
            setStatuses(response?.data?.statuses?.map(e => ({
                ...e,
                editing: false,
                creatingReason: false,
                reasons: e?.reasons?.map(r => ({...r, editing: false,})) ?? []
            })) ?? [])
        }
        setLoading(false)
    }, [isMounted])

    /**
     * As soon as the component mounts:
     * fetches the statuses and reasons from the server
     */
    useEffect(() => {
        void getStatusesAndReasons();
    }, [getStatusesAndReasons])

    /**
     * Create a new status with the given values.
     * @param {any} values the form values
     * @param {formApi} form the form
     * @return {Promise<boolean>}
     */
    const createStatus = async (values, form) => {
        setCreating(true)
        const forApi = {
            title: values.newStatus,
            isActive: true,
        }
        const response = await Api.createPartSupportStatus(forApi)
        if (!isMounted()) return false
        if (response?.resultFlag) {
            setStatuses(prevState => [({
                ...response.data,
                editing: false,
                creatingReason: false,
                reasons: []
            }), ...prevState])
            form.change('newStatus', undefined)
            form.resetFieldState('newStatus')
        }
        setCreating(false)
        return false
    }

    /**
     * Create a new reason with the given values from the form.
     * @param {any} values the values for creating a reason (name, code, reqStatusId)
     * @return {Promise<void>}
     */
    const createReason = async (values) => {
        setStatuses(prevState => prevState.map(e => e.id === values.reqStatusId ? {
            ...e,
            creatingReason: true
        } : e))
        const forApi = {
            isActive: true,
            ...values,
        }
        const response = await Api.createPartSupportReason(forApi)
        if (!isMounted()) return
        if (response?.resultFlag) {
            setStatuses(prevState => prevState.map(e => e.id === values.reqStatusId
                ? {
                    ...e,
                    reasons: [
                        {...response.data, editing: false,},
                        ...e.reasons
                    ]
                }
                : e
            ))
        }
        setStatuses(prevState => prevState.map(e => e.id === values.reqStatusId ? {
            ...e,
            creatingReason: false
        } : e))
    }

    /**
     * Updates a status.
     * @param {any}status the updated version of the status. used as the request data.
     * @return {Promise<void>}
     */
    const updateStatus = async (status) => {
        setStatuses(prevState => prevState.map(e => e.id === status.id ? {
            ...e,
            editing: true
        } : e))
        const response = await Api.updatePartSupportStatus(status)
        if (!isMounted()) return
        if (response?.resultFlag) {
            setStatuses(prevState => prevState.map(e => e.id === status.id ? {
                ...e,
                ...status
            } : e))
        }
        setStatuses(prevState => prevState.map(e => e.id === status.id ? {
            ...e,
            editing: false
        } : e))
    }

    /**
     * Updates a reason belonging to a status
     * @param {{reqStatusId: number}} reason the updated version of reason. used as a request data
     * @return {Promise<void>}
     */
    const updateReason = async (reason) => {
        setStatuses(prevState => prevState.map(e => e.id === reason.reqStatusId
            ? {
                ...e,
                reasons: e.reasons.map(r => r.id === reason.id
                    ? {
                        ...r,
                        editing: true
                    }
                    : r
                )
            } : e))
        const response = await Api.updatePartSupportReason(reason)
        if (!isMounted()) return
        if (response?.resultFlag) {
            setStatuses(prevState => prevState.map(e => e.id === reason.reqStatusId
                ? {
                    ...e,
                    reasons: e.reasons.map(r => r.id === reason.id
                        ? {
                            ...r,
                            ...reason,
                        }
                        : r
                    )
                } : e))
        }
        setStatuses(prevState => prevState.map(e => e.id === reason.reqStatusId
            ? {
                ...e,
                reasons: e.reasons.map(r => r.id === reason.id
                    ? {
                        ...r,
                        editing: false
                    }
                    : r
                )
            } : e))
    }

    return (
        <>
            <Paper className='paper w-100 px-3 py-2 flex-wrap justify-content-between align-items-start'>
                <Col xs={12}>
                    <p className={'text-lg font-weight-bold mt-4 mb-4'}>
                        Requests Statuses And Reasons
                    </p>
                </Col>
                <Form
                    className='d-flex flex-wrap w-100 align-items-center'
                    onSubmit={createStatus}
                    validate={validate}
                    initialValues={{}}
                    render={({values, form}) => {
                        return (
                            <>
                                <Col className={'pl-3'}>
                                    <OutlinedInputForm
                                        name={'newStatus'}
                                        placeholder={'title of the new status'}
                                        label={"New Status"}
                                        required={required.newStatus}
                                        value={values.newStatus ?? ''}
                                        onChange={(e) => form.change(e.target.name, e.target.value)}
                                    />
                                </Col>
                                <Col xs={'auto'}>
                                    <Button
                                        className='px-5'
                                        type={'submit'}
                                        disabled={creating || loading}
                                        color={'primary'}
                                        variant={'contained'}
                                    >
                                        {
                                            creating
                                                ? "Submitting..."
                                                : "Submit"
                                        }
                                    </Button>
                                </Col>
                            </>
                        )
                    }}
                />
                <Col xs={12} className={'mt-3 d-flex flex-wrap'}>
                    {
                        loading
                            ? Array(5).fill(null).map((e, i) => (
                                <div key={i} className={'loading normal w-100 mb-3'}>
                                    <div/>
                                </div>
                            ))
                            : statuses?.length < 1
                                ? <TryAgain
                                    text={"There is currently no statuses submitted. you can add one using the form above."}
                                    buttonText={'Reload'}
                                    onClick={getStatusesAndReasons}
                                />
                                : statuses?.map(status => (
                                    <WarrantyAndReturnStatusCard
                                        key={status.id}
                                        status={status}
                                        updateReason={updateReason}
                                        updateStatus={updateStatus}
                                        createReason={createReason}
                                        showIsActive
                                    />
                                ))
                    }
                </Col>
            </Paper>
        </>
    )
}


export default WarrantyAndReturnStatusesViewRequestStatusesSection;
